/**
 * Centralized API client with cookie-based authentication
 * Replaces manual JWT Bearer token authentication with HTTP-only cookies
 */

import { API_BASE_URL } from './apiConfig';
import { isAuth401ExemptRequest } from './authFetchInterceptor';
import { isRequiredPasswordChangeLogoutInProgress } from './passwordChangeLogout';

interface FetchOptions extends RequestInit {
  headers?: HeadersInit;
}

const CSRF_COOKIE = 'XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const RETRYABLE_STATUS = new Set([429, 502, 503]);
const RETRYABLE_BACKOFF_MS = [2_000, 4_000, 8_000];

export function isRetryableStatus(status: number): boolean {
  return RETRYABLE_STATUS.has(status);
}

export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined' || !document.cookie) {
    return undefined;
  }
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function isRefreshRequest(url: string): boolean {
  return url.includes('/api/auth/refresh');
}

function isMutatingMethod(method: string): boolean {
  return MUTATING_METHODS.has(method.toUpperCase());
}

/** Server CSRF ignore list — do not prime or retry these as first-mutation CSRF. */
export function isCsrfIgnoredRequest(url: string): boolean {
  return /\/api\/auth\/(login|refresh|mfa|forgot-password|redeem-invitation|validate-invitation|validate-invitation-token|check-invitation-token|accept-terms|clever)\b/.test(url)
    || /\/api\/(webhooks\/stripe|subscriptions\/(subscribe|checkout|check-email|clever-school-availability)|contact|newsletter)\b/.test(url);
}

let csrfPrimeInFlight: Promise<void> | null = null;

async function primeCsrfCookie(): Promise<void> {
  if (readCookie(CSRF_COOKIE)) {
    return;
  }
  if (!csrfPrimeInFlight) {
    csrfPrimeInFlight = fetch(`${API_BASE_URL}/api/auth/check`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then(() => undefined)
      .catch(() => undefined)
      .finally(() => {
        csrfPrimeInFlight = null;
      });
  }
  await csrfPrimeInFlight;
}

function withCsrfHeader(headers: Headers, method: string): void {
  if (!isMutatingMethod(method)) {
    return;
  }
  const token = readCookie(CSRF_COOKIE);
  if (token) {
    headers.set(CSRF_HEADER, token);
  }
}

/** Attach X-XSRF-TOKEN after xhr.open() and before xhr.send() for progress uploads. */
export function applyCsrfToXhr(xhr: XMLHttpRequest): void {
  const token = readCookie(CSRF_COOKIE);
  if (token) {
    xhr.setRequestHeader(CSRF_HEADER, token);
  }
}

function redirectToExpiredLogin(): void {
  localStorage.removeItem('userId');
  localStorage.removeItem('hasProfile');
  localStorage.removeItem('termsAndConditionsAccepted');
  localStorage.removeItem('loggedInUserId');
  localStorage.removeItem('authProvider');
  localStorage.removeItem('hasLoggedInBefore');
  window.location.href = '/login?session_expired=true';
}

export type CredentialedXhrOptions = {
  method: string;
  url: string;
  body?: XMLHttpRequestBodyInit | Document | null;
  onUploadProgress?: (pct: number) => void;
};

function openCredentialedXhr(options: CredentialedXhrOptions): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method, options.url);
    xhr.withCredentials = true;
    applyCsrfToXhr(xhr);
    if (options.onUploadProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          options.onUploadProgress?.(Math.round((event.loaded / event.total) * 100));
        }
      };
    }
    xhr.onload = () => resolve(xhr);
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(options.body ?? null);
  });
}

let refreshInFlight: Promise<boolean> | null = null;

function refreshAccessSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = Promise.resolve()
      .then(() => fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      }))
      .then((refresh) => Boolean(refresh?.ok))
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/**
 * Cookie + CSRF XHR with the same leftover-access refresh as {@link apiClient}.
 */
export async function sendCredentialedXhr(
  options: CredentialedXhrOptions,
  isAuthRetry = false,
  isCsrfRetry = false,
): Promise<XMLHttpRequest> {
  const method = (options.method || 'GET').toUpperCase();
  const shouldPrimeCsrf = isMutatingMethod(method)
    && !isCsrfIgnoredRequest(options.url)
    && !isCsrfRetry;
  if (shouldPrimeCsrf && !readCookie(CSRF_COOKIE)) {
    await primeCsrfCookie();
  }
  const sentCsrf = Boolean(isMutatingMethod(method) && readCookie(CSRF_COOKIE));
  const xhr = await openCredentialedXhr(options);
  if (xhr.status === 403 && shouldPrimeCsrf && !sentCsrf) {
    await primeCsrfCookie();
    if (readCookie(CSRF_COOKIE)) {
      return sendCredentialedXhr(options, isAuthRetry, true);
    }
    return xhr;
  }
  if (xhr.status !== 401 || isRequiredPasswordChangeLogoutInProgress()) {
    return xhr;
  }
  if (isAuthRetry) {
    redirectToExpiredLogin();
    throw new Error("Session expired");
  }
  if (!(await refreshAccessSession())) {
    redirectToExpiredLogin();
    throw new Error("Session expired");
  }
  return sendCredentialedXhr(options, true, isCsrfRetry);
}

/**
 * Makes an authenticated API request with credentials (cookies) included
 * @param endpoint - The API endpoint (e.g., '/api/users/profile')
 * @param options - Fetch options
 * @returns Promise<Response>
 */
export const apiClient = async (
  endpoint: string,
  options: FetchOptions = {}
): Promise<Response> => {
  return apiClientInternal(endpoint, options, false);
};

async function apiClientInternal(
  endpoint: string,
  options: FetchOptions,
  isAuthRetry: boolean,
  isCsrfRetry = false,
  retryableAttempt = 0,
): Promise<Response> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  const method = (options.method || 'GET').toUpperCase();
  const shouldPrimeCsrf = isMutatingMethod(method)
    && !isCsrfIgnoredRequest(url)
    && !isCsrfRetry;
  if (shouldPrimeCsrf && !readCookie(CSRF_COOKIE)) {
    await primeCsrfCookie();
  }

  const headers = new Headers(options.headers);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (!headers.has('Content-Type') && options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const sentCsrf = Boolean(isMutatingMethod(method) && readCookie(CSRF_COOKIE));
  withCsrfHeader(headers, method);

  const response = await fetch(url, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  if (response.status === 403 && shouldPrimeCsrf && !sentCsrf) {
    await primeCsrfCookie();
    if (readCookie(CSRF_COOKIE)) {
      return apiClientInternal(endpoint, options, isAuthRetry, true, retryableAttempt);
    }
    return response;
  }

  if (
    method === "GET"
    && isRetryableStatus(response.status)
    && retryableAttempt < RETRYABLE_BACKOFF_MS.length
  ) {
    await new Promise((resolve) => setTimeout(resolve, RETRYABLE_BACKOFF_MS[retryableAttempt]));
    return apiClientInternal(endpoint, options, isAuthRetry, isCsrfRetry, retryableAttempt + 1);
  }

  if (response.status !== 401 || isRequiredPasswordChangeLogoutInProgress()) {
    return response;
  }
  if (isAuth401ExemptRequest(url)) {
    return response;
  }
  if (isRefreshRequest(url) || isAuthRetry) {
    redirectToExpiredLogin();
    throw new Error('Session expired');
  }

  if (await refreshAccessSession()) {
    return apiClientInternal(endpoint, options, true, isCsrfRetry);
  }
  redirectToExpiredLogin();
  throw new Error('Session expired');
}

/**
 * Helper method for GET requests
 */
export const apiGet = async (endpoint: string, options: FetchOptions = {}): Promise<Response> => {
  return apiClient(endpoint, { ...options, method: 'GET' });
};

/**
 * Helper method for POST requests
 */
export const apiPost = async (
  endpoint: string,
  body?: any,
  options: FetchOptions = {}
): Promise<Response> => {
  return apiClient(endpoint, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * Helper method for PUT requests
 */
export const apiPut = async (
  endpoint: string,
  body?: any,
  options: FetchOptions = {}
): Promise<Response> => {
  return apiClient(endpoint, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * Helper method for DELETE requests
 */
export const apiDelete = async (endpoint: string, options: FetchOptions = {}): Promise<Response> => {
  return apiClient(endpoint, { ...options, method: 'DELETE' });
};
